/**
 * Usage Analytics page component.
 * Displays usage tracking data with charts, metrics, and filters.
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { usageService } from '../services/usageService';
import {
  AggregationPeriod,
  UsageType,
  type UsageAggregationDto,
  type UsageSummaryDto,
  type PagedUsageRecords,
} from '../types/usage';

// Charts
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';

/**
 * Usage Analytics page component.
 */
export const UsageAnalyticsPage = () => {
  const [usageType, setUsageType] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days ago
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 50;

  // Format date to YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 6,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Fetch usage summary
  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery<UsageSummaryDto>({
    queryKey: ['usageSummary', usageType, startDate, endDate],
    queryFn: async () => {
      return usageService.getUsageSummary({
        usageType: usageType ? Number(usageType) as UsageType : undefined,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      });
    },
    enabled: !!startDate && !!endDate,
  });

  // Fetch usage records
  const {
    data: records,
    isLoading: recordsLoading,
    error: recordsError,
  } = useQuery<PagedUsageRecords>({
    queryKey: ['usageRecords', usageType, startDate, endDate, pageNumber],
    queryFn: async () => {
      return usageService.getUsageRecords({
        usageType: usageType ? Number(usageType) as UsageType : undefined,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        pageNumber,
        pageSize,
      });
    },
    enabled: !!startDate && !!endDate,
  });

  // Fetch aggregations (for future use - can be used for trend charts)
  const aggregationUsageType: UsageType = usageType ? (Number(usageType) as UsageType) : UsageType.LLMTokens;
  const {
    data: aggregations,
    isLoading: aggregationsLoading,
    error: aggregationsError,
  } = useQuery<UsageAggregationDto[]>({
    queryKey: ['usageAggregations', aggregationUsageType, startDate, endDate],
    queryFn: async () => {
      return usageService.getUsageAggregations({
        usageType: aggregationUsageType,
        period: AggregationPeriod.Daily,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      });
    },
    enabled: !!startDate && !!endDate,
  });

  const trendChartData = (aggregations ?? []).map((a) => ({
    date: new Date(a.periodStart).toLocaleDateString(),
    quantity: a.totalQuantity,
    cost: a.totalCost,
  }));

  const handleApplyFilters = () => {
    refetchSummary();
    setPageNumber(1);
  };

  const getUsageTypeLabel = (type: UsageType | number) => {
    const typeNum = typeof type === 'number' ? type : Number(type);
    switch (typeNum) {
      case UsageType.LLMTokens:
        return 'LLM Tokens';
      case UsageType.Storage:
        return 'Storage';
      case UsageType.ApiCalls:
        return 'API Calls';
      case UsageType.ToolExecutions:
        return 'Tool Executions';
      case UsageType.ChatCompaction:
        return 'Chat Compaction';
      case UsageType.ImageCompaction:
        return 'Image Compaction';
      case UsageType.ImageDescription:
        return 'Image Description';
      default:
        return 'Unknown';
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
          Usage Analytics
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track and analyze usage of tokens, storage, API calls, and tool executions.
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                select
                fullWidth
                label="Usage Type"
                value={usageType}
                onChange={(e) => setUsageType(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value={String(UsageType.LLMTokens)}>LLM Tokens</MenuItem>
                <MenuItem value={String(UsageType.Storage)}>Storage</MenuItem>
                <MenuItem value={String(UsageType.ApiCalls)}>API Calls</MenuItem>
                <MenuItem value={String(UsageType.ToolExecutions)}>Tool Executions</MenuItem>
                <MenuItem value={String(UsageType.ChatCompaction)}>Chat Compaction</MenuItem>
                <MenuItem value={String(UsageType.ImageCompaction)}>Image Compaction</MenuItem>
                <MenuItem value={String(UsageType.ImageDescription)}>Image Description</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                type="date"
                fullWidth
                label="Start Date"
                value={formatDate(startDate)}
                onChange={(e) => setStartDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                type="date"
                fullWidth
                label="End Date"
                value={formatDate(endDate)}
                onChange={(e) => setEndDate(new Date(e.target.value))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleApplyFilters}
                disabled={summaryLoading || recordsLoading}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Quantity
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {formatNumber(summary.totalQuantity)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Cost
                </Typography>
                <Typography variant="h4" fontWeight={600} color="primary">
                  {formatCurrency(summary.totalCost)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Record Count
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {formatNumber(summary.recordCount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Usage Type
                </Typography>
                <Chip
                  label={getUsageTypeLabel(summary.usageType)}
                  color="primary"
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Loading State */}
      {(summaryLoading || recordsLoading || aggregationsLoading) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {(summaryError || recordsError || aggregationsError) && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {summaryError?.message || recordsError?.message || (aggregationsError as any)?.message || 'Failed to load usage data'}
        </Alert>
      )}

      {/* Trend Charts */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Trends (Daily)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing quantity (left axis) and cost (right axis) for{' '}
            {aggregationUsageType === UsageType.LLMTokens
              ? 'LLM Tokens'
              : aggregationUsageType === UsageType.ApiCalls
                ? 'API Calls'
                : aggregationUsageType === UsageType.Storage
                  ? 'Storage'
                  : 'Tool Executions'}
            .
          </Typography>

          <Box sx={{ height: 320 }}>
            {trendChartData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No aggregation data available for the selected range.
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="quantity" stroke="#7367F0" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="cost" stroke="#28C76F" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Breakdown by Agent */}
      {summary && summary.byAgent.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Usage by Agent
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Records</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.byAgent.map((item) => (
                    <TableRow key={item.agentDefinitionId}>
                      <TableCell>{item.agentDefinitionName}</TableCell>
                      <TableCell align="right">{formatNumber(item.totalQuantity)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      <TableCell align="right">{formatNumber(item.recordCount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Breakdown by Model */}
      {summary && summary.byModel.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Usage by Model
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Records</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.byModel.map((item) => (
                    <TableRow key={item.modelId}>
                      <TableCell>{item.modelName}</TableCell>
                      <TableCell align="right">{formatNumber(item.totalQuantity)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      <TableCell align="right">{formatNumber(item.recordCount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Usage Records Table */}
      {records && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Usage Records
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          No usage records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.items.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{new Date(record.recordDate).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Chip
                            label={getUsageTypeLabel(record.usageType)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{record.agentDefinitionName || '-'}</TableCell>
                        <TableCell>{record.modelName || '-'}</TableCell>
                        <TableCell align="right">{formatNumber(record.quantity)}</TableCell>
                        <TableCell align="right">{formatCurrency(record.cost)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {records.totalCount > pageSize && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {((pageNumber - 1) * pageSize) + 1} to {Math.min(pageNumber * pageSize, records.totalCount)} of {formatNumber(records.totalCount)} records
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={pageNumber === 1}
                    onClick={() => setPageNumber(pageNumber - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={pageNumber * pageSize >= records.totalCount}
                    onClick={() => setPageNumber(pageNumber + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
